import { z } from "zod";

import { referenceTypeEnum } from "../../enums/referenceTypeEnum";
import { sectionItemTypeEnum } from "../../enums/sectionItemTypeEnum";
import {
  sectionItemChallengeSchema,
  sectionItemCollectionSchema,
} from "../collection/sectionItemCollectionSchema";
import { communitySchema } from "../community/communitySchema";
import { deeplinkSchema } from "../deeplinkSchema";
import { prayerSchema } from "../prayer/prayerSchema";
import { sectionItemPrayerSchema } from "../prayer/sectionItemPrayerSchema";
import { radioStationSchema } from "../radio-station/radioStationSchema";
import { wordSchema } from "../wordSchema";
import { dailyQuoteSchema } from "./dailyQuoteSchema";
import { triviaLeaderboardSchema } from "./triviaLeaderboardSchema";
import { triviaGameSchema } from "./triviaSchema";

export const baseSectionItemSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  item_type: referenceTypeEnum,
  reference_id: z.number(),
  type: sectionItemTypeEnum,
});

const collectionAndSessionSchema = z.object({
  collection: sectionItemCollectionSchema,
  prayer: sectionItemPrayerSchema.nullable(),
  parish: z.any().nullable(),
});

export const collectionAndSessionSectionItemSchema =
  baseSectionItemSchema.extend({
    reference_type: referenceTypeEnum.extract(["collection_and_session"]),
    reference: collectionAndSessionSchema,
  });

export const deeplinkCardSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["deeplink_card"]),
  reference: deeplinkSchema,
});

export const collectionSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["collection"]),
  reference: sectionItemCollectionSchema,
});

export const prayerSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["prayer"]),
  reference: sectionItemPrayerSchema,
});

export const challengeSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["challenge"]),
  reference: sectionItemChallengeSchema,
});

export const hallowPlusBannerSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["hallow_plus_banner"]),
  reference: z.literal(null),
});

export const moodCheckSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["moodcheck"]),
  reference: z.literal(null),
});

export const triviaSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["trivia"]),
  reference: triviaGameSchema,
});

export const triviaLeaderboardSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["trivia_leaderboard"]),
  reference: z.object({
    game: triviaGameSchema,
    leaderboard: triviaLeaderboardSchema,
  }),
});

export const theWordSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["the_word"]),
  reference: z.object({
    game: wordSchema,
  }),
});

export const dailyQuoteSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["daily_quote"]),
  reference: dailyQuoteSchema,
});

export const contentItemSectionItemSchema = z.discriminatedUnion(
  "reference_type",
  [
    baseSectionItemSchema.extend({
      reference_type: referenceTypeEnum.extract(["collection"]),
      reference: sectionItemCollectionSchema,
    }),
    baseSectionItemSchema.extend({
      reference_type: referenceTypeEnum.extract(["prayer"]),
      reference: sectionItemPrayerSchema,
    }),
  ],
);

export const textFirstSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["text_first_content"]),
  reference: z.object({
    title: z.string(),
    description: z.string(),
    snippet: z.string(),
    image_url: z.string(),
    deeplink: z.string(),
  }),
});

export const assignmentSectionItemSchema = baseSectionItemSchema.extend({
  reference_type: referenceTypeEnum.extract(["assignment"]),
  reference: z.object({
    community: communitySchema,
    content: prayerSchema,
    content_type: z.literal("prayer"),
    due_at: z.iso.datetime(),
    id: z.number(),
    is_completed: z.boolean(),
  }),
});

export const sectionItemSchema = z.discriminatedUnion("reference_type", [
  collectionAndSessionSectionItemSchema,
  deeplinkCardSectionItemSchema,
  collectionSectionItemSchema,
  prayerSectionItemSchema,
  challengeSectionItemSchema,
  moodCheckSectionItemSchema,
  hallowPlusBannerSectionItemSchema,
  triviaSectionItemSchema,
  triviaLeaderboardSectionItemSchema,
  dailyQuoteSectionItemSchema,
  theWordSectionItemSchema,
  baseSectionItemSchema.extend({
    reference_type: referenceTypeEnum.extract(["radio_station"]),
    reference: radioStationSchema,
  }),
  textFirstSectionItemSchema,
  assignmentSectionItemSchema,
  // TODO: Change these from any to actual types once we start implementing these features in webv2
  baseSectionItemSchema.extend({
    reference_type: referenceTypeEnum.extract([
      "suggested_routine",
      "prayer_goal",
      "daily",
      "create_group",
      "invite_to_group",
      "alerts",
      "campaign",
      "community_challenge",
      "community_collection",
      "create_campaign_prompt",
      "create_campaign_prompt_v2_final",
      "text",
      "community_intention_prompt",
      "community_hosted_collection",
      "routine_item",
      "empty",
      "streak",
      "user_generated_text",
      "community_intention",
    ]),
    reference: z.any(),
  }),
]);

export type SectionItem = z.infer<typeof sectionItemSchema>;
export type ChallengeSectionItem = z.infer<typeof challengeSectionItemSchema>;
export type ChallengeItemSectionItem = z.infer<
  typeof collectionAndSessionSectionItemSchema
>;
export type DeeplinkCardSectionItem = z.infer<
  typeof deeplinkCardSectionItemSchema
>;
export type CollectionSectionItem = z.infer<typeof collectionSectionItemSchema>;
export type PrayerSectionItem = z.infer<typeof prayerSectionItemSchema>;
export type MoodcheckSectionItem = z.infer<typeof moodCheckSectionItemSchema>;
export type BannerSectionItem = z.infer<
  typeof hallowPlusBannerSectionItemSchema
>;
export type TheWordSectionItem = z.infer<typeof theWordSectionItemSchema>;
export type TriviaSectionItem = z.infer<typeof triviaSectionItemSchema>;
export type TriviaLeaderboardSectionItem = z.infer<
  typeof triviaLeaderboardSectionItemSchema
>;
export type DailyQuoteSectionItem = z.infer<typeof dailyQuoteSectionItemSchema>;
export type TextFirstSectionItem = z.infer<typeof textFirstSectionItemSchema>;
export type AssignmentSectionItem = z.infer<typeof assignmentSectionItemSchema>;
